/**
 * What we ask the encoder to produce, and why it is keyed on the capture size.
 *
 * The ladder this replaces derived every rung from one number — the quality
 * preset's `maxBitrate` — by dividing it by 16, 4 and 1. That is fine at 720p
 * and nonsense below it: on the Low preset (320x180 @ 150 kbps) it asked for a
 * bottom rung of 9.4 kbps at 80x45 and a middle rung of 37.5 kbps at 160x90.
 * Nothing can decode those usefully, but we paid to encode them and to upload
 * them anyway.
 *
 * So rungs are absolute, and the number of them varies: a small capture gets
 * fewer. Below 640 there is no ladder at all — one encoding, no simulcast, and
 * therefore no wasted encoder instances. The scale factors are chosen so the
 * bottom rung lands near 320 wide in every profile (1280/4, 1920/6, 3840/12),
 * which is the size a grid thumbnail actually needs.
 *
 * Ported from edumeet's `encodingsHandler.tsx`, numbers included. Two bugs in
 * the original are deliberately not carried over — see `chooseProfile`.
 *
 * Note there are no `rid` fields. mediasoup-client overwrites them with
 * r0/r1/r2 whenever there is more than one encoding (Chrome74.js:200-204), so
 * the 'q'/'h'/'f' rids the old ladder set never reached the wire.
 */

export type LadderEncoding = {
  maxBitrate: number
  scaleResolutionDownBy: number
  // Optional, set by the caller after the ladder is built — see useLocalMedia.ts.
  networkPriority?: RTCPriorityType
}

/** Keyed by the capture's long edge. Ordered low rung first, native last. */
const SIMULCAST_PROFILES: Record<number, readonly LadderEncoding[]> = {
  320: [
    { scaleResolutionDownBy: 1, maxBitrate: 150_000 },
  ],
  640: [
    { scaleResolutionDownBy: 2, maxBitrate: 150_000 },
    { scaleResolutionDownBy: 1, maxBitrate: 500_000 },
  ],
  1280: [
    { scaleResolutionDownBy: 4, maxBitrate: 150_000 },
    { scaleResolutionDownBy: 2, maxBitrate: 500_000 },
    { scaleResolutionDownBy: 1, maxBitrate: 1_200_000 },
  ],
  1920: [
    { scaleResolutionDownBy: 6, maxBitrate: 150_000 },
    { scaleResolutionDownBy: 3, maxBitrate: 500_000 },
    { scaleResolutionDownBy: 1, maxBitrate: 3_500_000 },
  ],
  3840: [
    { scaleResolutionDownBy: 12, maxBitrate: 150_000 },
    { scaleResolutionDownBy: 6, maxBitrate: 500_000 },
    { scaleResolutionDownBy: 1, maxBitrate: 10_000_000 },
  ],
}

const PROFILE_KEYS = Object.keys(SIMULCAST_PROFILES)
  .map(Number)
  .sort((a, b) => a - b)

/**
 * The smallest profile that can carry `size`, so a 1000-wide capture gets the
 * 1280 ladder with its top rung at native resolution rather than being upscaled
 * into the 640 one.
 *
 * Two deviations from edumeet's `chooseEncodings`, both deliberate:
 *
 *  1. It returns a fresh array. edumeet mutates the module-level profile via
 *     `encodings.push(...)`, so its 320 entry permanently grows a second rung
 *     after the first call and every later caller sees a two-rung "single"
 *     profile.
 *  2. A capture wider than the largest profile falls back to that profile.
 *     edumeet breaks out of its loop with an empty array and produces a track
 *     with no encodings at all.
 *
 * edumeet also duplicates a lone encoding to work around an old mediasoup bug.
 * We are on mediasoup-client 3.20 and a single encoding produces correctly, so
 * that workaround is not carried over — it would reintroduce the second rung
 * this whole module exists to remove.
 */
function chooseProfile(size: number): LadderEncoding[] {
  const key = PROFILE_KEYS.find((k) => k >= size) ?? PROFILE_KEYS[PROFILE_KEYS.length - 1]
  return SIMULCAST_PROFILES[key].map((e) => ({ ...e }))
}

/**
 * `width`/`height` must come from the track's own `getSettings()`, not from the
 * quality preset. The preset is what we asked the camera for; the camera is
 * free to hand back something else, and sizing the ladder to the request rather
 * than the result is how you end up with rungs that do not match the frames.
 *
 * Falls back to the 1280 profile when the track reports no dimensions yet,
 * which is what a camera does for a moment after `getUserMedia` resolves.
 */
export function getEncodings(
  width: number | undefined,
  height: number | undefined,
  opts: { simulcast: boolean } = { simulcast: true },
): LadderEncoding[] {
  const size = Math.max(width ?? 0, height ?? 0) || 1280
  const profile = chooseProfile(size)

  // One rung at the profile's top bitrate. Used when simulcast is off, which
  // today means a device we judged too weak to encode three streams — so it
  // gets the one it would have sent anyway, not a throttled version of it.
  if (!opts.simulcast) {
    return [{ scaleResolutionDownBy: 1, maxBitrate: profile[profile.length - 1].maxBitrate }]
  }

  return profile
}

/** Exposed for tests and for logging what we actually asked for. */
export const __profilesForTest = SIMULCAST_PROFILES

/**
 * The ordering the whole ladder depends on, made checkable.
 *
 * `remapEncodings` matches desired rungs onto a live sender's encodings by
 * POSITION, which is only correct while both are ordered low rung first. That
 * was a comment; a comment cannot fail. Called from the tests over every
 * profile, so reordering a table silently is not possible.
 */
export function assertLadderOrdering(rungs: readonly LadderEncoding[]): void {
  for (let i = 1; i < rungs.length; i++) {
    if (!(rungs[i].scaleResolutionDownBy < rungs[i - 1].scaleResolutionDownBy)) {
      throw new Error(`ladder not ordered: scaleResolutionDownBy must strictly decrease (index ${i})`)
    }
    if (!(rungs[i].maxBitrate > rungs[i - 1].maxBitrate)) {
      throw new Error(`ladder not ordered: maxBitrate must strictly increase (index ${i})`)
    }
  }
}
